import type { BlockNoteEditor } from "@blocknote/core";
import { applyCommentMarkToSelection } from "./editorCommentMarks";
import {
  findThreadBounds,
  findThreadRange,
  getMarkThreadId,
  rangeHasOtherThread,
} from "./editorCommentThread";
import { COMMENT_THREAD_STYLE } from "./editorSchema";
import { canUseEditorView } from "./editorComments";

const markMatchesThread = (
  mark: { type: { name: string }; attrs: Record<string, unknown> },
  threadId: string
) => getMarkThreadId(mark) === threadId;

const isRangeFullyMarked = (
  editor: BlockNoteEditor,
  threadId: string,
  from: number,
  to: number
) => {
  let fully = true;
  editor.prosemirrorState.doc.nodesBetween(from, to, (node) => {
    if (!node.isText || !node.text) return true;
    if (!node.marks.some((mark) => markMatchesThread(mark, threadId))) {
      fully = false;
    }
    return true;
  });
  return fully;
};

const quoteFromRange = (
  editor: BlockNoteEditor,
  from: number,
  to: number
) =>
  editor.prosemirrorState.doc
    .textBetween(from, to, " ")
    .replace(/\s+/g, " ")
    .trim();

export const removeCommentMarksForThread = (
  editor: BlockNoteEditor,
  threadId: string
) => {
  if (!canUseEditorView(editor)) return;

  const ranges: Array<{ from: number; to: number }> = [];
  editor.prosemirrorState.doc.descendants((node, pos) => {
    if (!node.marks.some((mark) => markMatchesThread(mark, threadId))) {
      return true;
    }
    ranges.push({ from: pos, to: pos + node.nodeSize });
    return true;
  });
  if (!ranges.length) return;

  const schema = editor.prosemirrorState.schema;
  const commentMark = schema.marks.comment;
  const threadStyle = schema.marks[COMMENT_THREAD_STYLE];

  try {
    editor.transact((tr) => {
      ranges.forEach(({ from, to }) => {
        if (commentMark) {
          tr.removeMark(
            from,
            to,
            commentMark.create({ orphan: false, threadId })
          );
        }
        if (threadStyle) {
          tr.removeMark(
            from,
            to,
            threadStyle.create({ stringValue: threadId })
          );
        }
      });
    });
  } catch {
    return;
  }
};

/** Keep the pending thread mark on the live text as the user types. */
export const syncPendingThreadAnchor = (
  editor: BlockNoteEditor,
  threadId: string
): { from: number; to: number; text: string } | null => {
  if (!canUseEditorView(editor)) return null;

  const range = findThreadBounds(editor, threadId);
  if (!range) return null;

  let { from, to } = range;
  const selection = editor.prosemirrorState.selection;
  if (selection.empty) {
    const pos = selection.from;
    const atOtherThread = rangeHasOtherThread(
      editor,
      Math.min(from, pos),
      Math.max(to, pos),
      threadId
    );
    if (!atOtherThread && pos >= range.from - 1 && pos <= range.to + 1) {
      from = Math.min(from, pos);
      to = Math.max(to, pos);
    }
  }

  if (rangeHasOtherThread(editor, from, to, threadId)) {
    const tight = findThreadRange(editor, threadId);
    if (!tight) return null;
    from = tight.from;
    to = tight.to;
  }

  if (!isRangeFullyMarked(editor, threadId, from, to)) {
    applyCommentMarkToSelection(editor, threadId, { from, to }, {
      stopPending: false,
    });
  }

  const text = quoteFromRange(editor, from, to);
  if (!text) return null;
  return { from, to, text };
};
